import { Heading, Stack } from "@biom3/react";
import { Card, CardContent, Typography } from "@mui/material";

type Prize = {
  amount: number;
  name: string;
  type: string;
};

type PrizesWonCardProps = {
  prizes: Prize[];
  className?: string;
  isLoading?: boolean;
};

export const PrizesWonCard = ({
  prizes,
  className,
  isLoading = false,
}: PrizesWonCardProps) => {
  // Hardcoded list of all available physical prizes
  const allAvailablePhysicalPrizes: Prize[] = [
    { name: "Meta Quest 3", type: "physical", amount: 1 },
    { name: "PlayStation 5", type: "physical", amount: 1 },
    { name: "Series X", type: "physical", amount: 1 },
    { name: "Switch 2", type: "physical", amount: 1 },
    { name: "$20 Steam", type: "physical", amount: 20 },
    { name: "$25 Xbox", type: "physical", amount: 25 },
    { name: "$20 PlayStation", type: "physical", amount: 20 },
    { name: "$20 Apple", type: "physical", amount: 20 },
  ];

  // Separate prizes by type
  const physicalPrizes = prizes.filter((p) => p.type === "physical");
  const gemPrizes = prizes.filter((p) => p.type === "gems");
  const imxPrizes = prizes.filter((p) => p.type === "imx");

  // Calculate totals
  const totalGems = gemPrizes.reduce((sum, p) => sum + p.amount, 0);
  const totalImx = imxPrizes.reduce((sum, p) => sum + p.amount, 0);

  // Calculate total gift cards (sum all gift card amounts)
  const totalGiftCards = prizes
    .filter(
      (p) =>
        p.type === "physical" &&
        (p.name.toLowerCase().includes("$20 steam") ||
          p.name.toLowerCase().includes("$25 xbox") ||
          p.name.toLowerCase().includes("$20 playstation") ||
          p.name.toLowerCase().includes("$20 apple") ||
          p.name.toLowerCase().includes("gift card")),
    )
    .reduce((sum, p) => sum + p.amount, 0);

  // Check if any gift cards were won
  const isGiftCardWon = totalGiftCards > 0;

  // Find unwon physical prizes (exclude gems, IMX, and gift cards)
  const wonPrizeNames = new Set(prizes.map((p) => p.name));
  const unwonHardwarePrizes = allAvailablePhysicalPrizes.filter(
    (p) =>
      !wonPrizeNames.has(p.name) &&
      (p.name.includes("Meta Quest") ||
        p.name.includes("PlayStation") ||
        p.name.includes("Series X") ||
        p.name.includes("Switch")) &&
      // Always exclude gift cards from unwon hardware prizes (they are handled separately)
      !p.name.toLowerCase().includes("$20 steam") &&
      !p.name.toLowerCase().includes("$25 xbox") &&
      !p.name.toLowerCase().includes("$20 playstation") &&
      !p.name.toLowerCase().includes("$20 apple") &&
      !p.name.toLowerCase().includes("gift card"),
  );

  // Map prize names to icon files
  const getIconForPrize = (prize: Prize): string => {
    const name = prize.name.toLowerCase();

    // Physical prizes
    if (name.includes("meta quest 3"))
      return "/icons/prize-wheel/meta-quest.webp";
    if (name.includes("playstation 5"))
      return "/icons/prize-wheel/hardware-ps5.webp";
    if (name.includes("series x"))
      return "/icons/prize-wheel/hardware-xbox.webp";
    if (name.includes("switch 2"))
      return "/icons/prize-wheel/hardware-switch.webp";

    // Gift cards - use generic gift card icon for all gift cards
    if (name.includes("$20 steam") || name.includes("steam"))
      return "/icons/prize-wheel/gift-card.webp";
    if (name.includes("$25 xbox") || name.includes("xbox"))
      return "/icons/prize-wheel/gift-card.webp";
    if (name.includes("$20 playstation") || name.includes("playstation"))
      return "/icons/prize-wheel/gift-card.webp";
    if (name.includes("$20 apple") || name.includes("apple"))
      return "/icons/prize-wheel/gift-card.webp";
    if (name.includes("gift card")) return "/icons/prize-wheel/gift-card.webp";

    // Nothing prize
    if (name.includes("nothing")) return "/icons/prize-wheel/nothing.webp";

    // Gem prizes
    if (prize.type === "gems") {
      return "/assets/icons/gem.webp";
    }

    // IMX prizes
    if (prize.type === "imx") {
      return "/assets/icons/imx-token.svg";
    }

    // Default fallback
    return "/assets/icons/gift.svg";
  };

  // Get icon for aggregated gems
  const getGemsIcon = (amount: number): string => {
    return "/assets/icons/gem.webp";
  };

  // Get icon for aggregated IMX
  const getImxIcon = (amount: number): string => {
    return "/assets/icons/imx-token.svg";
  };

  // Map prize names to icon files for UNWON prizes (uses generic icons)
  const getIconForUnwonPrize = (prize: Prize): string => {
    const name = prize.name.toLowerCase();

    // Physical hardware prizes - use specific icons
    if (name.includes("meta quest 3"))
      return "/icons/prize-wheel/meta-quest.webp";
    if (name.includes("playstation 5"))
      return "/icons/prize-wheel/hardware-ps5.webp";
    if (name.includes("series x"))
      return "/icons/prize-wheel/hardware-xbox.webp";
    if (name.includes("switch 2"))
      return "/icons/prize-wheel/hardware-switch.webp";

    // Gift cards - use GENERIC gift card icon for unwon prizes
    if (name.includes("$20 steam") || name.includes("steam"))
      return "/icons/prize-wheel/gift-card.webp";
    if (name.includes("$25 xbox") || name.includes("xbox"))
      return "/icons/prize-wheel/gift-card.webp";
    if (name.includes("$20 playstation") || name.includes("playstation"))
      return "/icons/prize-wheel/gift-card.webp";
    if (name.includes("$20 apple") || name.includes("apple"))
      return "/icons/prize-wheel/gift-card.webp";
    if (name.includes("gift card")) return "/icons/prize-wheel/gift-card.webp";

    // Nothing prize
    if (name.includes("nothing")) return "/icons/prize-wheel/nothing.webp";

    // Default fallback
    return "/icons/prize-wheel/generic-gem.webp";
  };

  // Create a unified array of all prizes to display
  const allPrizesToDisplay = [];

  // Add won gems (if any)
  if (totalGems > 0) {
    allPrizesToDisplay.push({
      type: "gems",
      name: "Gems",
      amount: totalGems,
      isWon: true,
      icon: getGemsIcon(totalGems),
    });
  }

  // Add won IMX (if any)
  if (totalImx > 0) {
    allPrizesToDisplay.push({
      type: "imx",
      name: "IMX",
      amount: totalImx,
      isWon: true,
      icon: getImxIcon(totalImx),
    });
  }

  // Add won physical prizes (excluding gift cards) - group by name and sum amounts
  const physicalPrizeGroups = new Map<
    string,
    { prize: Prize; totalAmount: number }
  >();

  for (const prize of physicalPrizes) {
    // Skip gift cards - they will be handled separately
    const isGiftCard =
      prize.name.toLowerCase().includes("$20 steam") ||
      prize.name.toLowerCase().includes("$25 xbox") ||
      prize.name.toLowerCase().includes("$20 playstation") ||
      prize.name.toLowerCase().includes("$20 apple") ||
      prize.name.toLowerCase().includes("gift card");

    if (!isGiftCard) {
      const existing = physicalPrizeGroups.get(prize.name);
      if (existing) {
        existing.totalAmount += prize.amount;
      } else {
        physicalPrizeGroups.set(prize.name, {
          prize: prize,
          totalAmount: prize.amount,
        });
      }
    }
  }

  // Add grouped physical prizes to display
  for (const [prizeName, group] of physicalPrizeGroups) {
    allPrizesToDisplay.push({
      type: "physical",
      name: group.prize.name,
      amount: group.totalAmount,
      isWon: true,
      icon: getIconForPrize(group.prize),
    });
  }

  // Add gift cards (single entry - only show if won)
  if (isGiftCardWon) {
    allPrizesToDisplay.push({
      type: "gift-card",
      name: "Gift Cards",
      amount: totalGiftCards,
      isWon: true,
      icon: "/icons/prize-wheel/gift-card.webp",
    });
  }

  // Add unwon hardware prizes
  for (const prize of unwonHardwarePrizes) {
    allPrizesToDisplay.push({
      type: "physical",
      name: prize.name,
      amount: 0,
      isWon: false,
      icon: getIconForUnwonPrize(prize),
    });
  }

  // Add unwon gems (if no gems were won)
  if (totalGems === 0) {
    allPrizesToDisplay.push({
      type: "gems",
      name: "Gems",
      amount: 0,
      isWon: false,
      icon: getGemsIcon(0),
    });
  }

  // Add unwon IMX (if no IMX was won)
  if (totalImx === 0) {
    allPrizesToDisplay.push({
      type: "imx",
      name: "IMX",
      amount: 0,
      isWon: false,
      icon: getImxIcon(0),
    });
  }

  // Add unwon gift cards (if no gift cards were won)
  if (!isGiftCardWon) {
    allPrizesToDisplay.push({
      type: "gift-card",
      name: "Gift Cards",
      amount: 0,
      isWon: false,
      icon: "/icons/prize-wheel/gift-card.webp",
    });
  }

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        height: "100px",
        background: "#252525",
        borderRadius: "12px",
        position: "relative",
      }}
      className={className}
    >
      <CardContent
        sx={{ p: 3, height: "100%", display: "flex", alignItems: "center" }}
      >
        <Stack
          direction="row"
          gap="base.spacing.x4"
          alignItems="center"
          sx={{
            display: "flex",
            width: "100%",
          }}
        >
          <div className="relative flex items-center justify-center size-12 rounded-[8px] bg-[#131313] shrink-0">
            <img
              src="/assets/icons/gift.svg"
              alt="Prizes"
              className="w-6 h-6 object-contain"
            />
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className="flex flex-col gap-1">
              <Typography variant="caption" color="text.secondary">
                PRIZES WON
              </Typography>

              {isLoading ? (
                <div className="w-[100%] flex flex-col [@media(min-width:1500px)]:flex-row items-center justify-center [@media(min-width:1500px)]:gap-6">
                  <div className="flex items-center justify-evenly w-full [@media(min-width:1500px)]:w-auto gap-6">
                    {[
                      "loading-skeleton-1",
                      "loading-skeleton-2",
                      "loading-skeleton-3",
                      "loading-skeleton-4",
                    ].map((key) => (
                      <div key={key} className="flex items-center gap-1">
                        <div className="w-8 h-8 bg-white/20 rounded animate-pulse" />
                        <div className="w-6 h-4 bg-white/20 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                  <div className="text-center mt-2">
                    <Typography variant="caption" color="text.secondary">
                      Loading prizes...
                    </Typography>
                  </div>
                </div>
              ) : (
                <div className="w-[100%] flex flex-col [@media(min-width:1500px)]:flex-row items-center justify-center [@media(min-width:1500px)]:gap-6">
                  {/* First line - 4 prizes on 1200px+ screens */}
                  <div className="flex items-center justify-evenly w-full [@media(min-width:1500px)]:w-auto gap-6">
                    {allPrizesToDisplay.slice(0, 4).map((prize, index) => (
                      <div
                        key={`first-${prize.type}-${index}`}
                        className="flex items-center gap-1"
                      >
                        <img
                          src={prize.icon}
                          alt={prize.name}
                          className={`${prize.type === "gems" || prize.type === "imx" ? "w-6 h-6" : "w-8 h-8"} object-contain ${!prize.isWon ? "opacity-60" : ""}`}
                        />
                        <Heading
                          size="small"
                          weight="bold"
                          sx={{
                            color: "base.color.text.on-neutral",
                            opacity: !prize.isWon ? 0.6 : 1,
                          }}
                        >
                          {prize.amount}
                        </Heading>
                      </div>
                    ))}
                  </div>

                  {/* Second line - remaining prizes on 1200px+ screens */}
                  {allPrizesToDisplay.length > 4 && (
                    <div className="flex items-center justify-evenly w-full [@media(min-width:1500px)]:w-auto gap-6">
                      {allPrizesToDisplay.slice(4, 8).map((prize, index) => (
                        <div
                          key={`second-${prize.type}-${index}`}
                          className="flex items-center gap-1"
                        >
                          <img
                            src={prize.icon}
                            alt={prize.name}
                            className={`${prize.type === "gems" || prize.type === "imx" ? "w-6 h-6" : "w-8 h-8"} object-contain ${!prize.isWon ? "opacity-60" : ""}`}
                          />
                          <Heading
                            size="small"
                            weight="bold"
                            sx={{
                              color: "base.color.text.on-neutral",
                              opacity: !prize.isWon ? 0.6 : 1,
                            }}
                          >
                            {prize.amount}
                          </Heading>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Stack>
      </CardContent>
    </Card>
  );
};
